import 'intl-tel-input/build/css/intlTelInput.css'
import 'intl-tel-input/build/js/utils.js'

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import iti from 'intl-tel-input'
import { normalizePhoneNumber } from 'utils/formatting/normalizePhoneNumber'
import styles from './PhoneInput.module.sass'
import { useTranslation } from 'react-i18next'
import { Country } from 'constants/country'
import { logger } from 'utils/logging'

/**
 * @interface Props Input properties
 */
export interface Props extends Omit<React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'onChange'> {
  /** The additional classes to append */
  className?: string
  /** intl-tel-input options object */
  options?: iti.Options
  /** Handler for value change event */
  onChange: (value: string) => void
  /** Optional event handler for phone validity updates */
  onValidityChange?: (isValid: boolean, errorMessage: string | undefined) => void
  /** Set the initial country */
  initialCountry: string
}

/** Codes the platform uses that the widget knows under another name. */
const PHONE_COUNTRY_ALIASES: Record<string, string> = { uk: 'gb' }

/** Codes already reported, so a bad code warns once per page load, not on every render. */
const reportedUnknownCountries = new Set<string>()

/**
 * The country, if the phone widget has data for it; otherwise undefined.
 *
 * The widget throws on a code it does not know, and an address the platform could
 * not geocode carries the placeholder country `XX` — which crashed the whole page.
 */
export const supportedPhoneCountry = (code?: string): string | undefined => {
  if (!code) return undefined
  const lower = code.toLowerCase()
  const iso2 = PHONE_COUNTRY_ALIASES[lower] ?? lower
  const countries = window.intlTelInputGlobals?.getCountryData()
  if (countries?.some((country) => country.iso2 === iso2)) return iso2 === lower ? code : iso2.toUpperCase()
  if (!reportedUnknownCountries.has(code)) {
    reportedUnknownCountries.add(code)
    logger.warn('Phone input got a country it has no data for', {
      context: { component: 'PhoneInput', action: 'supportedPhoneCountry', metadata: { code, widgetLoaded: !!countries } },
    })
  }
  return undefined
}

/**
 * @component   
 * Phone number input that auto-formats text and handles country code displaying.
 * Reworked clone of original TelephoneInput fixing reactivity issues for controlled values.
 * 
 * @example
 * <PhoneInput
 *  value={variable}
 *  onChange={(formattedValue) => console.log(`formattedValue: ${formattedValue}`)}
 *  onValidityChange={({ isValid, errorMessage }) => setIsValid(isValid)}
 * />
 */
export const PhoneInput: React.FC<Props> = (props) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const itiPlugin = useRef<iti.Plugin | undefined>(undefined)
  const [isPluginReady, setIsPluginReady] = useState(false)
  const { t } = useTranslation('telephone_input')
  // An unknown country falls back to Switzerland, as every caller does for a missing one.
  // Not '': with no country the widget picks the first in its list (Afghanistan, +93).
  const knownCountry = useMemo(() => supportedPhoneCountry(props.initialCountry) ?? Country.CH, [props.initialCountry])

  const _getCountrifiedNumber = useCallback((bareNumber: string) => {
    // Sanitize BEFORE formatting so the value written to state/payload can never
    // retain letters or other junk. intl-tel-input's formatNumber silently returns
    // the raw unmodified string on parse failure, so without this the displayed
    // (sanitized) value and the submitted value silently diverge — see ISSUE-2542.
    return intlTelInputUtils.formatNumber(
      normalizePhoneNumber(bareNumber),
      itiPlugin.current?.getSelectedCountryData().iso2 || knownCountry,
      intlTelInputUtils.numberFormat.E164
    )
  }, [knownCountry])

  useLayoutEffect(() => {
    if (!inputRef.current) return
    if (itiPlugin.current) return

    itiPlugin.current = iti(inputRef.current, {
      initialCountry: knownCountry,
    })
    setIsPluginReady(true)
  }, [inputRef, knownCountry])

  // Propagate country change from props
  useLayoutEffect(() => {
    if (!itiPlugin.current) return
    if (!knownCountry) return
    itiPlugin.current.setCountry(knownCountry)
  }, [knownCountry])

  // Sync value to plugin, validate, and propagate validity — in a single effect
  // to guarantee validation runs AFTER the plugin has the current value
  const onValidityChangeRef = useRef(props.onValidityChange)
  onValidityChangeRef.current = props.onValidityChange

  useEffect(() => {
    if (!itiPlugin.current) return

    itiPlugin.current.setNumber(normalizePhoneNumber(props.value?.toString() || ''))

    const isValid = itiPlugin.current.isValidNumber()
    const errorCode = itiPlugin.current.getValidationError()
    const errorMessage = t(`errors.${errorCode}`, t(`errors.${4}`, '')) || undefined

    onValidityChangeRef.current?.(isValid, errorMessage)
  }, [props.value, t, isPluginReady])

  useEffect(() => {
    // Any, because otherwise it complaints about unknown custom event - not sure how to extend it without extending all inputs' events
    const handler = (e: any) => {
      props.onChange(_getCountrifiedNumber(e.target?.value))
    }

    const telInput = inputRef.current
    if (!telInput) return

    telInput.addEventListener('countrychange', handler)
    return () => {
      telInput.removeEventListener('countrychange', handler)
    }
  }, [_getCountrifiedNumber, props])

  const { options, value, initialCountry, onValidityChange, ...passedProps } = { ...props }

  return (
    <div className={`${styles.phoneInput} fs-mask`}>
      <input
        {...passedProps}
        type={props.type || 'tel'}
        className={props.className}
        ref={inputRef}
        onChange={(e) => {
          props.onChange(_getCountrifiedNumber(e.target.value))
        }}
        onFocus={(e) => props.onFocus?.(e)}
        onBlur={(e) => {
          e.target.value = e.target.value.replace(/\s+/gmi, ' ').trim()
          props.onBlur?.(e)
        }}
      />
    </div>
  )
}
